import { useEffect, useRef, useState, useCallback } from 'react';
import { formatCurrency } from '@sailpoint/bva-core';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { formatCompactCurrency } from '../libs/calculations.js';

const COLORS = [
  '#0033a1', '#0071CE', '#54C0E8', '#00B294', '#93D500', '#F2A900', '#FF5F00', '#E62E2E',
  '#8A3FFC', '#d60eb5', '#CC27B0', '#33B1FF', '#415364', '#718096', '#A0AEC0',
];

const DonutChart = ({ results, onFilterChange, canvasRef }) => {
  const [legendData, setLegendData] = useState([]);
  const [tooltipState, setTooltipState] = useState({
    visible: false,
    top: '0px',
    left: 'auto',
    right: 'auto',
    color: '#000',
    title: '',
    body: '',
  });
  const chartInstanceRef = useRef(null);
  const [fontReady, setFontReady] = useState(false);
  const centerTextRef = useRef('');

  const centerTextPlugin = useRef({
    id: 'centerText',
    afterDraw(chart) {
      if (chart.config.type !== 'doughnut' || !chart.data.datasets.length) return;
      const ctx = chart.ctx;
      const { top, left, width, height } = chart.chartArea;
      const centerX = left + width / 2;
      const centerY = top + height / 2;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.font = '1.75rem poppins';
      ctx.fillStyle = '#1e293b';
      ctx.fillText(centerTextRef.current, centerX, centerY - 10);
      ctx.font = '0.875rem sans-serif';
      ctx.fillStyle = '#64748b';
      ctx.fillText('Total Savings', centerX, centerY + 15);
    },
  }).current;

  const externalTooltipHandler = useCallback((context) => {
    const { chart, tooltip } = context;
    if (tooltip.opacity === 0) {
      setTooltipState((prev) => ({ ...prev, visible: false }));
      return;
    }
    const dataPoint = tooltip.dataPoints[0];
    if (!dataPoint) return;

    const { label, parsed } = dataPoint;
    const color = tooltip.labelColors[0]?.backgroundColor || '#000';
    const allData = chart.data.datasets[0].data;
    const visibleTotal = allData.reduce(
      (sum, val, i) => (chart.getDataVisibility(i) ? sum + Number(val) : sum),
      0
    );
    const valueStr = formatCurrency(parsed, results?.currencySymbol);
    let bodyStr = valueStr;
    if (visibleTotal > 0) {
      const percentage = (parsed / visibleTotal) * 100;
      bodyStr = `${valueStr} (${percentage.toFixed(1)}%)`;
    }

    const offset = 15;
    const tooltipWidth = 200;
    const tooltipHeight = 80;
    let left = tooltip.caretX + offset;
    let right = 'auto';
    if (left + tooltipWidth > chart.canvas.width) {
      left = 'auto';
      right = `${chart.canvas.width - tooltip.caretX + offset}px`;
    }
    if (typeof left === 'number' && left < 0) left = offset;
    let top = tooltip.caretY;
    if (top + tooltipHeight > chart.canvas.height) {
      top = chart.canvas.height - tooltipHeight - offset;
    }
    if (top < 0) top = offset;

    setTooltipState({
      visible: true,
      top: `${top}px`,
      left: typeof left === 'number' ? `${left}px` : left,
      right,
      color,
      title: label || '',
      body: bodyStr,
    });
  }, [results?.currencySymbol]);

  useEffect(() => {
    let isMounted = true;
    const loadFont = async () => {
      if (document.fonts) {
        await document.fonts.load('1rem "Poppins"');
        await document.fonts.ready;
      }
      if (isMounted) setFontReady(true);
    };
    loadFont();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!fontReady) return;

    const renderChart = () => {
      if (!canvasRef?.current || !results?.donutChartData?.length) {
        if (chartInstanceRef.current) chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
        setLegendData([]);
        centerTextRef.current = formatCompactCurrency(0, results?.currencySymbol);
        return;
      }

      if (chartInstanceRef.current) chartInstanceRef.current.destroy();

      const sortedData = [...results.donutChartData].sort((a, b) => b.savings - a.savings);
      const chartData = {
        labels: sortedData.map((d) => d.benefit),
        datasets: [
          {
            data: sortedData.map((d) => d.savings),
            backgroundColor: COLORS.slice(0, sortedData.length),
            borderColor: '#ffffff',
            borderWidth: 4,
            borderRadius: 8,
            hoverOffset: 10,
            hoverBorderColor: '#fff',
          },
        ],
      };

      centerTextRef.current = formatCompactCurrency(
        sortedData.reduce((sum, item) => sum + item.savings, 0),
        results?.currencySymbol
      );

      setLegendData(
        sortedData.map((d, i) => ({
          text: d.benefit,
          fillStyle: chartData.datasets[0].backgroundColor[i],
          hidden: false,
        }))
      );

      chartInstanceRef.current = new Chart(canvasRef.current, {
        type: 'doughnut',
        data: chartData,
        plugins: [centerTextPlugin, ChartDataLabels],
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: '75%',
          layout: { padding: 35 },
          plugins: {
            legend: {
              display: false,
              labels: {
                font: {
                  family: 'Poppins, Arial, sans-serif',
                  size: 12,
                  weight: 'bold',
                },
              },
            },
            datalabels: {
              formatter: (value, context) => {
                const chart = context.chart;
                if (!chart.getDataVisibility(context.dataIndex)) return null;
                const allData = chart.data.datasets[0].data;
                const visibleTotal = allData.reduce(
                  (sum, cv, i) => (chart.getDataVisibility(i) ? sum + Number(cv) : sum),
                  0
                );
                if (visibleTotal === 0) return null;
                const percentage = (value / visibleTotal) * 100;
                if (percentage < 2) return null;
                return `${percentage.toFixed(0)}%`;
              },
              anchor: 'start',
              align: 'end',
              color: '#475569',
              clamp: false,
              font: {
                family: 'Poppins, Arial, sans-serif',
                weight: '600',
                size: 11,
              },
              offset: 32,
              padding: 0,
              connector: {
                display: true,
                type: 'line',
                length: 25,
                width: 1,
                color: '#cbd5e1',
              },
            },
            tooltip: { enabled: false, external: externalTooltipHandler },
          },
        },
      });

      chartInstanceRef.current.update();
    };

    renderChart();

    return () => {
      if (chartInstanceRef.current) chartInstanceRef.current.destroy();
    };
  }, [fontReady, results, canvasRef, centerTextPlugin, externalTooltipHandler]);

  const handleLegendClick = (index) => {
    const chart = chartInstanceRef.current;
    if (!chart) return;

    chart.toggleDataVisibility(index);

    const newLegend = legendData.map((item, i) =>
      i === index ? { ...item, hidden: !chart.getDataVisibility(i) } : item
    );
    setLegendData(newLegend);

    const newTotal = chart.data.datasets[0].data.reduce(
      (sum, val, i) => (chart.getDataVisibility(i) ? sum + Number(val) : sum),
      0
    );
    centerTextRef.current = formatCompactCurrency(newTotal, results?.currencySymbol);
    chart.update();

    if (onFilterChange) {
      const visibleBenefitNames = chart.data.labels.filter((_, i) =>
        chart.getDataVisibility(i)
      );
      onFilterChange(visibleBenefitNames);
    }
  };

  return (
    <div className="bg-white border border-gray-200 p-6 rounded-xl shadow-sm h-full flex flex-col">
      <h3 className="text-xl text-gray-700 mb-4 text-center flex-shrink-0">
        5-Year Savings Breakdown
      </h3>
      <div
        className="grid grid-cols-1 md:grid-cols-2 gap-6 flex-grow items-center min-h-0 h-full"
        id="donutChartContent"
      >
        <div className="relative h-full flex flex-col min-h-[250px]">
          {results?.donutChartData?.length > 0 ? (
            <div className="relative h-full flex flex-col">
              <canvas
                ref={canvasRef}
                id="donutChartCanvas"
                style={{ width: '100%', height: '100%', minHeight: '250px', display: 'block' }}
              />
              {tooltipState.visible && (
                <div
                  className="flex absolute rounded-md shadow-lg overflow-hidden transition-opacity duration-100 pointer-events-none"
                  style={{
                    top: tooltipState.top,
                    left: tooltipState.left,
                    right: tooltipState.right,
                    transform: 'translateY(-50%)',
                  }}
                >
                  <div className="w-1.5" style={{ backgroundColor: tooltipState.color }} />
                  <div className="bg-slate-800 text-white p-3 text-left">
                    <div className="font-bold text-sm mb-1">{tooltipState.title}</div>
                    <div className="text-xs">{tooltipState.body}</div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 mb-3 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth="1.5"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z"
                />
              </svg>
              <p className="font-medium">No Savings Data</p>
              <p className="text-sm">Select benefits and calculate to see your breakdown.</p>
            </div>
          )}
        </div>
        <div className="relative h-full overflow-y-auto pl-12 pr-2">
          {legendData.length > 0 ? (
            <ul className="space-y-2">
              {legendData.map((item, index) => (
                <li
                  key={item.text}
                  className={`flex items-center cursor-pointer transition-colors duration-200 ${item.hidden ? 'text-gray-400' : ''
                    }`}
                  onClick={() => handleLegendClick(index)}
                  onKeyDown={(e) => e.key === 'Enter' && handleLegendClick(index)}
                  tabIndex={0}
                  role="button"
                >
                  <span
                    className={`w-3 h-3 rounded-full mr-3 flex-shrink-0 ${item.hidden ? 'opacity-25' : ''
                      }`}
                    style={{ backgroundColor: item.fillStyle }}
                  />
                  <span className="text-xs">{item.text}</span>
                </li>
              ))}
            </ul>
          ) : !results?.donutChartData?.length ? (
            <div className="flex items-center justify-center h-full text-center text-gray-400 text-sm">
              Legend will appear here.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default DonutChart;